/* BattleFX — the ephemeral animation layer:
 *   - <DamageNumber />  floating +/- N near the affected creature
 *   - <CriticalCallout /> full-screen banner: FIRST STRIKE, CRITICAL, COMEBACK,
 *     MOMENTUM SHIFT, WEATHER ADVANTAGE, FINAL STAND
 *   - <AbilityOverlay /> darken + species/ability name flash
 *
 * Every element removes itself when its animation finishes; nothing
 * accumulates in memory.
 */
import { motion, AnimatePresence } from 'framer-motion';

const CALLOUT_COLORS = {
  'first-strike':      { text: 'FIRST STRIKE',      color: '#60a5fa', gradient: 'from-sky-400 to-blue-500' },
  'critical':          { text: 'CRITICAL',          color: '#f87171', gradient: 'from-rose-400 to-red-600' },
  'comeback':          { text: 'COMEBACK',          color: '#c084fc', gradient: 'from-fuchsia-400 to-purple-600' },
  'momentum':          { text: 'MOMENTUM SHIFT',    color: '#facc15', gradient: 'from-amber-300 to-yellow-500' },
  'weather-advantage': { text: 'WEATHER ADVANTAGE', color: '#a3e635', gradient: 'from-lime-300 to-emerald-500' },
  'final-stand':       { text: 'FINAL STAND',       color: '#fb923c', gradient: 'from-orange-300 to-rose-500' },
  'victory-blow':      { text: 'VICTORY',           color: '#fde047', gradient: 'from-yellow-200 to-amber-500' },
};

export function DamageNumber({ side, value, crit, kind = 'damage' }) {
  const sign = kind === 'heal' ? '+' : '-';
  const color = kind === 'heal' ? '#4ade80' : crit ? '#f87171' : '#fbbf24';
  const label = kind === 'miss' ? 'MISS' : crit ? `CRITICAL ${sign}${value}` : `${sign}${value}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.7 }}
      animate={{ opacity: [0, 1, 1, 0], y: [-8, -60, -80, -100], scale: [0.7, crit ? 1.4 : 1.15, 1, 0.9] }}
      transition={{ duration: 1.1, times: [0, 0.25, 0.7, 1] }}
      className="pointer-events-none absolute z-40 font-serif font-bold drop-shadow-lg select-none tabular-nums"
      style={{
        left: side === 'challenger' ? '25%' : '75%',
        top: '38%',
        color,
        fontSize: crit ? '2.2rem' : '1.6rem',
        transform: 'translate(-50%, -50%)',
        textShadow: `0 0 12px ${color}88, 0 2px 4px rgba(0,0,0,0.6)`,
      }}
      data-testid={`damage-${side}`}
    >
      {label}
    </motion.div>
  );
}

export function CriticalCallout({ kind, text }) {
  const meta = CALLOUT_COLORS[kind] || CALLOUT_COLORS.critical;
  return (
    <motion.div
      initial={{ opacity: 0, y: -30, scale: 0.5 }}
      animate={{
        opacity: [0, 1, 1, 0],
        y: [-30, 0, 0, 20],
        scale: [0.5, 1.1, 1, 0.95],
      }}
      transition={{ duration: 0.85, times: [0, 0.25, 0.7, 1] }}
      className="pointer-events-none absolute inset-x-0 top-1/3 flex justify-center z-40"
      data-testid={`callout-${kind}`}
    >
      <div className="relative px-6 py-2">
        <div
          className={`absolute inset-0 rounded-2xl blur-2xl opacity-70 bg-gradient-to-r ${meta.gradient}`}
        />
        <p
          className={`relative font-serif text-5xl leading-none tracking-tight bg-gradient-to-b ${meta.gradient} bg-clip-text text-transparent drop-shadow`}
          style={{ textShadow: `0 0 30px ${meta.color}` }}
        >
          {text || meta.text}
        </p>
      </div>
    </motion.div>
  );
}

export function AbilityOverlay({ side, name, actor }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 1, 1, 0] }}
      transition={{ duration: 0.75, times: [0, 0.2, 0.75, 1] }}
      className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center"
      data-testid={`ability-overlay-${side}`}
    >
      <div className="absolute inset-0 bg-black/70" />
      <motion.div
        initial={{ scale: 0.6, y: 30 }}
        animate={{ scale: [0.6, 1.1, 1], y: [30, -6, 0] }}
        transition={{ duration: 0.5 }}
        className="relative text-center"
      >
        <p className="text-[10px] uppercase tracking-[0.35em] text-emerald-300/80">
          {actor} · uses
        </p>
        <p className="font-serif text-5xl leading-none mt-1 bg-gradient-to-b from-emerald-200 to-emerald-400 bg-clip-text text-transparent drop-shadow">
          {name}
        </p>
        <div className="mt-3 flex justify-center gap-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <motion.span
              key={i}
              animate={{ opacity: [0, 1, 0], scale: [0.5, 1.4, 0.5] }}
              transition={{ duration: 0.6, delay: i * 0.1, repeat: Infinity, repeatDelay: 0.2 }}
              className="w-1.5 h-1.5 rounded-full bg-emerald-400"
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

export function FxLayer({ effects }) {
  return (
    <AnimatePresence>
      {effects.map((fx) => {
        if (fx.type === 'damage')   return <DamageNumber key={fx.id} {...fx} />;
        if (fx.type === 'miss')     return <DamageNumber key={fx.id} side={fx.side} value={0} kind="miss" />;
        if (fx.type === 'heal')     return <DamageNumber key={fx.id} side={fx.side} value={fx.value} kind="heal" />;
        if (fx.type === 'callout')  return <CriticalCallout key={fx.id} kind={fx.kind} text={fx.text} />;
        if (fx.type === 'ability')  return <AbilityOverlay key={fx.id} {...fx} />;
        return null;
      })}
    </AnimatePresence>
  );
}
